import { useState } from 'react';
import styles from './EnvBanner.module.css';

// Persistent "you are not on production" marker.
//
// Renders nothing when REACT_APP_APP_ENV is 'prod' (or unset, which is how the existing
// production Netlify site behaves until the variable is added there) - so this component is
// inert on the live site.
//
// Why it exists: once DEV, TEST and PROD look identical, the expensive mistakes are
// demoing fake data to a customer, filing a bug against the wrong environment, and
// "fixing" something on a database nobody ships from. A banner that cannot be permanently
// dismissed is the cheapest insurance against all three.
//
// CRA inlines process.env.REACT_APP_* at BUILD time, so this is fixed per deploy - each
// Netlify site sets its own value.
const ENV = (process.env.REACT_APP_APP_ENV || 'prod').toLowerCase();

const LABELS = {
  dev:   { name: 'DEV',   note: 'test data · resets without warning' },
  test:  { name: 'TEST',  note: 'test data · not real users' },
  local: { name: 'LOCAL', note: 'local backend' },
};

export default function EnvBanner() {
  // Collapse is per page-load only - deliberately not persisted, so the banner is always
  // back on the next navigation or refresh.
  const [collapsed, setCollapsed] = useState(false);

  if (ENV === 'prod' || ENV === 'production') return null;

  const label = LABELS[ENV] || { name: ENV.toUpperCase(), note: 'non-production' };

  if (collapsed) {
    return (
      <button
        type="button"
        className={styles.pill}
        onClick={() => setCollapsed(false)}
        title={`${label.name} environment - click to expand`}
      >
        {label.name}
      </button>
    );
  }

  return (
    <div className={styles.banner} role="status" aria-live="polite">
      <span className={styles.badge}>{label.name}</span>
      <span className={styles.note}>{label.note}</span>
      <button
        type="button"
        className={styles.close}
        onClick={() => setCollapsed(true)}
        aria-label="Collapse environment banner"
      >
        &times;
      </button>
    </div>
  );
}
